import { useMemo } from "react";

import { AI_SOURCE_AUDIO } from "src/core/lib/constants";
import api, { useSWR } from "../utils/axios";

const endpoint = "ai";
const baseUrl = `/api/${endpoint}/`;

export function createChat(data, config) {
  return api.post(`${baseUrl}chat`, data, config);
}

/**
 * Phase 1 of the voice flow: uploads a recorded audio and gets back only the recognized text, so the
 * user can confirm or correct it before anything is applied to the form. The request is
 * multipart/form-data with a single "audio" section — the form context now travels with the confirmed
 * transcript on `interpretTranscript` instead. The file name carries the extension the backend
 * validates against its allowed audio extensions.
 *
 * Returns { transcription, success }; success is false when nothing intelligible was recognized.
 */
export function transcriptSpeech(audio, fileName = "recording.webm") {
  const formData = new FormData();

  formData.append("audio", audio, fileName);

  return api.post(`${baseUrl}speech`, formData);
}

/**
 * Phase 2 of the voice flow: interprets the confirmed (possibly user-edited) transcript against the
 * form the user is on. `context` keeps the exact shape the multipart context section used to have,
 * with the numeric `source` inside it.
 *
 * Returns { transcription, source, success, action, fields }.
 */
export function interpretTranscript(transcript, context, source = AI_SOURCE_AUDIO.textField) {
  return api.post(`${baseUrl}speech/interpret`, {
    transcript,
    context: { ...context, source },
  });
}

export function useGetChatHistory(shouldFetch = false) {
  const url = `${baseUrl}chat/history`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(shouldFetch ? url : null);

  const memoizedValue = useMemo(
    () => ({
      history: data || [],
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function getChatById(id) {
  return api.get(`${baseUrl}chat/${id}`);
}

export function updateGptConfiguration(data) {
  return api.put(`${baseUrl}configuration`, data);
}

export function useGetGptConfiguration() {
  const url = `${baseUrl}configuration`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(url);

  const memoizedValue = useMemo(
    () => ({
      configuration: data,
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function postDocument(id, files, onUploadProgress) {
  const url = `${baseUrl}configuration/document`;

  return api.postForm(
    url,
    {
      files,
    },
    {
      onUploadProgress,
    }
  );
}

export function removeDocument(id) {
  return api.delete(`${baseUrl}configuration/document/${id}`);
}
